import type { CSSObject } from '@emotion/react'
import color from 'color'
import { Color, FontWeight, Spacing, Typeface, Typography } from '@opallabs/gem'
import { colors } from '@opallabs/lapidary'
import type { StyleProps } from '../types'

const userHeaderHeight = Spacing.M * 6.125
const panelTabsHeight = Spacing.M * 2.875
const subPanelTabsHeight = Spacing.M * 3.25

const subTabSelectionBorder = ({ theme }: StyleProps): string =>
  `2px solid ${Color[theme].Surface.N300}`

const borderBox: CSSObject = {
  boxSizing: 'border-box',

  '*': { boxSizing: 'border-box' },
}

const flexColumn: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
}

export const emptyPanel = ({ theme }: StyleProps): CSSObject => ({
  backgroundColor: Color[theme].Surface.N000,
  color: Color[theme].Text.Default,
  ...Typography.T500,
  alignItems: 'center',
  alignSelf: 'center',
  display: 'flex',
  fill: 'currentColor',
  flexDirection: 'column',
  blockSize: '100%',
  justifyContent: 'center',
  justifySelf: 'center',
  textRendering: 'optimizeLegibility',
  inlineSize: '100%',
})

export const drawer = ({ theme }: StyleProps): CSSObject => ({
  ...borderBox,
  ...flexColumn,
  backgroundColor: Color[theme].Surface.N000,
  blockSize: '100%',
  inlineSize: '100%',
})

export const userHeader: CSSObject = {
  blockSize: userHeaderHeight,
}

export const mainTabWrapper = ({ theme }: StyleProps): CSSObject => ({
  ...flexColumn,
  backgroundColor: Color[theme].Surface.N200,
  flex: 1,
  blockSize: `calc(100% - ${userHeaderHeight}px)`,
})

export const mainTabList = ({ theme }: StyleProps): CSSObject => ({
  backgroundColor: Color[theme].Surface.N200,
  padding: Spacing.S,
  margin: 0,
  display: 'flex',
  flexWrap: 'nowrap',
  blockSize: panelTabsHeight,
})

export const mainTabPanel = ({ theme }: StyleProps): CSSObject => ({
  '&.satellite_tabs__panel--selected': {
    ...flexColumn,
    backgroundColor: Color[theme].Surface.N000,
    flex: 1,
    blockSize: `calc(100% - ${panelTabsHeight}px)`,
  },
})

export const subTabWrapper: CSSObject = {
  ...flexColumn,
  blockSize: '100%',
}

export const subTabPanel: CSSObject = {
  '&.satellite_tabs__panel--selected': {
    ...flexColumn,
    flexGrow: 1,
    blockSize: `calc(100% - ${subPanelTabsHeight}px)`,
    overflowY: 'auto',
  },
}

export const subTabList = ({ theme }: StyleProps): CSSObject => ({
  backgroundColor: Color[theme].Surface.N000,
  borderBlockEnd: subTabSelectionBorder({ theme }),
  margin: Spacing.S * 1.25,
  paddingBlockEnd: Spacing.S * 1.25,
})

const unseenOverrides = ({ theme }: StyleProps): CSSObject => ({
  color: colors[theme].secondary.yellow100,
  '&:hover': {
    color: color(colors[theme].secondary.yellow100).darken(0.06).hex(),
  },
  '&:active': {
    color: color(colors[theme].secondary.yellow100).darken(0.12).hex(),
  },
})

export const subTab = ({
  hasUnseen,
  theme,
}: {
  hasUnseen: boolean
} & StyleProps): CSSObject => ({
  color: Color[theme].Text.Gray,
  fontFamily: Typeface.Default,
  fontSize: 11,
  fontWeight: FontWeight.Bold,
  blockSize: Spacing.S * 1.625,
  letterSpacing: 0,
  paddingBlock: Spacing.XS * 1.25,
  paddingInline: Spacing.XS * 1.25,
  textTransform: 'uppercase',
  position: 'relative',
  ...(hasUnseen && unseenOverrides({ theme })),

  '&:hover': {
    color: Color[theme].Text.Default,
  },
})

export const subTabSelected = ({ theme }: StyleProps): CSSObject => ({
  color: Color[theme].Text.Default,

  '&:after': {
    backgroundColor: Color[theme].Surface.N000,
    insetBlockEnd: Spacing.M * -1,
    content: '""',
    display: 'block',
    blockSize: Spacing.XS * 1.25,
    insetInlineStart: '50%',
    position: 'absolute',
    transform: 'translateX(-50%) rotate(45deg)',
    inlineSize: Spacing.XS * 1.25,
    borderInlineStart: subTabSelectionBorder({ theme }),
    borderBlockStart: subTabSelectionBorder({ theme }),
    borderRadius: 0,
    margin: 0,
  },
})

export const collapsibleList: CSSObject = {
  paddingBlockEnd: Spacing.M * 1.375,
}

export const collapsibleListTitle = ({ theme }: StyleProps): CSSObject => ({
  textTransform: 'uppercase',
  fontSize: 10,
  color: Color[theme].Text.Default,
})

export const title = ({ theme }: StyleProps): CSSObject => ({
  ...Typography.T500,
  fontKerning: 'normal',
  textRendering: 'optimizeLegibility',
  fontSize: 10,
  textTransform: 'uppercase',
  display: 'inline-block',
  marginBlockStart: Spacing.S * 1.25,
  marginInlineStart: Spacing.M * 1.25,
  color: Color[theme].Text.Default,
})
